import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TogglebuttonIcon } from '@bi-digital/icons';
import {
    Button,
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    useMediaQuery,
} from '@bi-digital/react';
import { FilterDropdowns, FilterGroups, type FiltersProps } from '../components/filters/Filters';
import { FormerStudentCard, type FormerStudent } from '../components/former-student-card/FormerStudentCard';
import styles from './page.module.css';

export type PageProps = {
    items: FormerStudent[];
    levels: string[];
    academicDegrees: string[];
};

export const Page = ({ items, levels, academicDegrees }: PageProps) => {
    const { t } = useTranslation('former-students');
    const isCompactScreen = useMediaQuery('(width < 1128px)');
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [level, setLevel] = useState('');
    const [selectedDegrees, setSelectedDegrees] = useState<string[]>([]);

    const itemsAtLevel = (forLevel: string) => items.filter((item) => !forLevel || item.levels.includes(forLevel));
    const degreesAtLevel = (forLevel: string) =>
        academicDegrees.filter((degree) =>
            itemsAtLevel(forLevel).some((item) => item.academicDegrees.includes(degree)),
        );

    const filteredItems = itemsAtLevel(level).filter(
        (item) =>
            selectedDegrees.length === 0 || selectedDegrees.some((degree) => item.academicDegrees.includes(degree)),
    );

    const changeLevel = (newLevel: string) => {
        setLevel(newLevel);
        const available = degreesAtLevel(newLevel);
        setSelectedDegrees(selectedDegrees.filter((degree) => available.includes(degree)));
    };

    const resetFilters = () => {
        setLevel('');
        setSelectedDegrees([]);
        setFilterDialogOpen(false);
    };

    const filterProps: FiltersProps = {
        levels,
        academicDegrees: degreesAtLevel(level),
        level,
        selectedDegrees,
        setLevel: changeLevel,
        setSelectedDegrees,
    };

    return (
        <div className={styles.page}>
            {isCompactScreen ? (
                <>
                    <Button
                        className={styles.filterButton}
                        onClick={() => setFilterDialogOpen(true)}
                        variant='flat'
                        endIcon={<TogglebuttonIcon aria-hidden='true' />}
                    >
                        {t('filter.toggleButton')}
                    </Button>
                    <Dialog
                        className={styles.filterDrawer}
                        open={filterDialogOpen}
                        onClose={() => setFilterDialogOpen(false)}
                        anchor='bottom'
                    >
                        <DialogCloseButton
                            aria-label={t('filter.closeButton')}
                            onClick={() => setFilterDialogOpen(false)}
                        />
                        <DialogHeader>{t('filter.header')}</DialogHeader>
                        <DialogContent>
                            <FilterGroups {...filterProps} />
                        </DialogContent>
                        <DialogFooter>
                            <Button className={styles.filterActionButton} variant='text' onClick={resetFilters}>
                                {t('filter.resetButton')}
                            </Button>
                            <Button className={styles.filterActionButton} onClick={() => setFilterDialogOpen(false)}>
                                {t('filter.applyButton', { count: filteredItems.length })}
                            </Button>
                        </DialogFooter>
                    </Dialog>
                </>
            ) : (
                <FilterDropdowns {...filterProps} />
            )}
            <ul className={styles.list} data-promo-card-list-variant='quadrat-1'>
                {filteredItems.map((item) => (
                    <FormerStudentCard key={item.url} student={item} />
                ))}
            </ul>
        </div>
    );
};
